import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { 
  Comment, 
  CommentRequest, 
  CommentUpdateRequest, 
  CommentResponse, 
  CommentListResponse
} from '../models/comment.models';

@Injectable({
  providedIn: 'root'
})
export class CommentsService {
  private readonly commentsUrl = environment.main_url + '/agilelegal/comments';

  constructor(private readonly http: HttpClient) { }

  /**
   * Get all comments
   */
  getAllComments(): Observable<CommentListResponse> {
    return this.http.get<CommentListResponse>(this.commentsUrl);
  }

  /**
   * Get comment by ID
   */
  getCommentById(id: number): Observable<CommentResponse> {
    return this.http.get<CommentResponse>(`${this.commentsUrl}/${id}`);
  }

  /**
   * Get comments by target (Task or Project)
   */
  getCommentsByTarget(targetType: string, targetId: number): Observable<CommentListResponse> {
    const params = new HttpParams()
      .set('targetType', targetType)
      .set('targetId', targetId.toString());
    
    return this.http.get<CommentListResponse>(`${this.commentsUrl}/target`, { params });
  }

  /**
   * Get replies for a specific comment
   */
  getCommentReplies(commentId: number): Observable<CommentListResponse> {
    return this.http.get<CommentListResponse>(`${this.commentsUrl}/${commentId}/replies`);
  }

  /**
   * Get comments by user
   */
  getCommentsByUser(userId: number): Observable<CommentListResponse> {
    return this.http.get<CommentListResponse>(`${this.commentsUrl}/user/${userId}`);
  }

  /**
   * Create a new comment
   */
  createComment(commentRequest: CommentRequest): Observable<CommentResponse> {
    return this.http.post<CommentResponse>(this.commentsUrl, commentRequest);
  }

  /**
   * Update an existing comment
   */
  updateComment(id: number, commentRequest: CommentUpdateRequest): Observable<CommentResponse> {
    return this.http.put<CommentResponse>(`${this.commentsUrl}/${id}`, commentRequest);
  }

  /**
   * Delete a comment (soft delete)
   */
  deleteComment(id: number): Observable<CommentResponse> {
    return this.http.delete<CommentResponse>(`${this.commentsUrl}/${id}`);
  }

  /**
   * Helper method to organize comments into threaded structure
   */
  organizeCommentsIntoThreads(comments: Comment[]): Comment[] {
    const commentMap = new Map<number, Comment>();
    const rootComments: Comment[] = [];

    // First pass: create map of all comments
    comments.forEach(comment => {
      comment.replies = [];
      commentMap.set(comment.id, comment);
    });

    // Second pass: organize into tree structure
    comments.forEach(comment => {
      if (comment.parentCommentId === null) {
        // Root level comment
        rootComments.push(comment);
      } else {
        // Reply to another comment
        const parent = commentMap.get(comment.parentCommentId);
        if (parent) {
          parent.replies = parent.replies || [];
          parent.replies.push(comment);
        }
      }
    });

    return rootComments;
  }

  /**
   * Helper method to check if user can edit comment
   * Only the user who created the comment can edit it (even admins can't edit others' comments)
   */
  canEditComment(comment: Comment, currentUserId: number, userRoles: string[]): boolean {
    // Only the comment creator can edit their own comments
    return comment.fkUserId === currentUserId;
  }

  /**
   * Helper method to check if user can delete comment
   * Comment creators can delete their own comments, and admins can delete any comment
   */
  canDeleteComment(comment: Comment, currentUserId: number, userRoles: string[]): boolean {
    // Comment creator can delete their own comments, or admins can delete any comment
    return comment.fkUserId === currentUserId || userRoles.includes('ADMIN');
  }
}
